<template>
  <div class="h-listl">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-listl__content">
      <div class="h-listl__head">
        <div>
          <h1 class="h-listl__title">Usage events</h1>
          <p class="h-listl__sub">{{ subtitle }}</p>
        </div>
      </div>

      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load usage events"
        :message="error"
        @retry="load"
      />
      <HermesEmptyState
        v-else-if="!loading && !rows.length"
        variant="first-run"
        title="No usage events yet"
        message="As consuming products report metered usage against their subscriptions, the raw event log appears here."
      />
      <div v-else class="h-listl__card">
        <HermesTabulator
          :rows="rows"
          :columns="columns"
          :loading="loading"
          storage-key="admin-usage-events"
          :initial-sort="{ column: 'event_timestamp', dir: 'desc' }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesUsageEventsList — Phase 5 lean usage-events list island. Replaces the old
// 6-tab dashboard's `usage` tab: a HermesTabulator over GET /admin/usage (last 200
// raw usage events).
export default {
  name: 'HermesUsageEventsList',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    vendorName: { type: String, default: '' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      rows: [],
      loading: true,
      error: null,
      crumbs: [{ label: 'Platform' }, { label: 'Usage events' }],
      columns: [
        { key: 'id', label: 'ID', width: 78, format: (v) => `<span style="font-family:var(--h-font-mono);color:var(--h-text-secondary)">#${v}</span>` },
        { key: 'subscription_id', label: 'Sub', width: 80, sorter: 'number', format: (v) => v ? `<span style="font-family:var(--h-font-mono);color:var(--h-text-secondary)">#${v}</span>` : '<span style="color:var(--h-text-tertiary)">—</span>' },
        { key: 'end_user_email', label: 'Customer', format: (v) => v || '<span style="color:var(--h-text-tertiary)">—</span>' },
        { key: 'metric_key', label: 'Metric', format: (v) => `<span style="font-family:var(--h-font-mono);font-size:12.5px">${v || '—'}</span>` },
        { key: 'metric_value', label: 'Value', hozAlign: 'right', width: 110, sorter: 'number', format: (v) => `<span style="font-family:var(--h-font-mono);font-variant-numeric:tabular-nums">${v != null ? parseFloat(v) : '—'}</span>` },
        { key: 'event_timestamp', label: 'When', width: 140, sorter: 'number', format: (v) => this.date(v) },
        { key: 'idempotency_key', label: 'Idempotency', format: (v) => v ? `<span style="font-family:var(--h-font-mono);font-size:11.5px;color:var(--h-text-tertiary)">${v.slice(0, 28)}${v.length > 28 ? '…' : ''}</span>` : '—' },
      ],
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const n = this.rows.length;
      return `${n} event${n === 1 ? '' : 's'}${n >= 200 ? ' (latest 200)' : ''}`;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/usage`);
        this.rows = data.items || [];
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load usage events${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    date(v) {
      if (!v) return '<span style="color:var(--h-text-tertiary)">—</span>';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-listl { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-listl__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-listl__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-listl__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-listl__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-listl__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-listl__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-listl__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
</style>
